import React, { useEffect, useRef, useState } from 'react';
import { Wind, Thermometer, Home, ArrowRight } from 'lucide-react';

const HowItWorksSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      icon: Wind,
      title: "Umweltenergie nutzen",
      description: "Eine Wärmepumpe nutzt kostenlose Energie aus Luft, Erde oder Wasser",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: Thermometer,
      title: "Effizient umwandeln", 
      description: "Die Wärmepumpe wandelt diese Energie hocheffizient in Heizwärme um",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: Home,
      title: "Komfortabel heizen",
      description: "Ihr Zuhause wird angenehm beheizt und Sie sparen dabei bis zu 80% Kosten",
      color: "from-brand-orange to-brand-orange-hover"
    }
  ];

  return (
    <section 
      id="how-it-works" 
      ref={sectionRef}
      className="relative py-20 bg-gradient-to-br from-white via-gray-50/50 to-blue-50/40 overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(249,250,251,0.6) 50%, rgba(239,246,255,0.4) 100%)'
      }}
    >
      {/* SECTION NUMBERING */}
      <div className="absolute top-4 left-4 z-[60] bg-red-600 text-white px-3 py-2 rounded-lg font-bold text-sm sm:text-base shadow-lg hidden">
        SEKTION 2: WIE ES FUNKTIONIERT
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 relative z-50">
              Was eine Wärmepumpe macht
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Eine Wärmepumpe ist hocheffiziente Technologie, die kostenlose Umweltenergie 
              in wohlige Wärme für Ihr Zuhause verwandelt.
            </p>
          </div>

          {/* Animated Steps */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {steps.map((step, index) => (
              <div
                key={index}
                className={`relative transition-all duration-1000 delay-${index * 200} ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              >
                <div className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 relative overflow-hidden">
                  {/* Background Gradient */}
                  <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${step.color} opacity-10 rounded-full -translate-y-8 translate-x-8`}></div>
                  
                  {/* Icon */}
                  <div className={`w-16 h-16 bg-gradient-to-br ${step.color} rounded-2xl flex items-center justify-center mb-6 relative z-10`}>
                    <step.icon className="w-8 h-8 text-white" />
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-xl font-bold text-gray-900 mb-4 relative z-50">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed relative z-10">
                    {step.description}
                  </p>
                  
                  {/* Step Number */}
                  <div className="absolute top-4 right-4 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-bold text-gray-600">{index + 1}</span>
                  </div>
                </div>
                
                {/* Arrow between steps */}
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-20">
                    <ArrowRight className="w-8 h-8 text-gray-300" />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* 3D Visualization Placeholder */}
          <div className={`transition-all duration-1000 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="bg-white rounded-3xl p-8 shadow-2xl border border-gray-100 relative">

              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 relative z-50">
                  So funktioniert eine Wärmepumpe
                </h3>
                <p className="text-gray-600">
                  Interaktive 3D-Visualisierung zeigt den Funktionsprozess
                </p>
              </div>
              
              {/* 3D Placeholder */}
              <div className="bg-white rounded-2xl p-8 h-80 flex items-center justify-center shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_green-heating/artifacts/gk0pfpic_waermepumpe-funktionsweise-1.png.webp"
                  alt="Wärmepumpe Funktionsweise - Schematische Darstellung"
                  title="Funktionsweise einer Wärmepumpe - technischer Kreislauf erklärt"
                  width="800"
                  height="500"
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              
              {/* Key Points */}
              <div className="grid md:grid-cols-3 gap-6 mt-8">
                <div className="text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Wind className="w-6 h-6 text-brand-green" />
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Funktioniert auch im Winter</h4>
                  <p className="text-sm text-gray-600">Selbst bei -20°C effizient</p>
                </div>
                
                <div className="text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Thermometer className="w-6 h-6 text-green-600" />
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Heizt & kühlt</h4>
                  <p className="text-sm text-gray-600">Ganzjähriger Komfort</p>
                </div>
                
                <div className="text-center">
                  <div className="w-12 h-12 bg-brand-orange/20 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Home className="w-6 h-6 text-brand-orange" />
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Warmwasser inklusive</h4>
                  <p className="text-sm text-gray-600">Alles aus einer Hand</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;